import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import I18n from 'i18n-js';
import SearchInput from '@App/components/search/SearchInput';
import { useAppSelector } from '@App/store';
import { clearSoundEffectCategory, setSoundEffectTerm } from '@App/store/actions/soundEffect';
import { openFilterModal } from '@App/store/actions/navigation';
import { SIZE_LG } from '@App/service/MobileWatcher';

/**
 * Search bar of the sound effects page (/effets-sonores), sibling of the music {@link SearchBar} inside
 * the fixed header: same white band in the same place, searching sound effects instead of music.
 *
 * Living in the header (and not in the page as it first did) is what makes it stay put when the
 * results scroll, and what keeps it from painting over the account dropdown — `.searchbar` outranks
 * the header in z-index, so a bar rendered in the page covered the very menu that lets one log out.
 *
 * The active category filter is recalled here as a removable chip, next to the text being typed, so
 * that both criteria of the search read in one place — as they do for music.
 */
export default function SoundEffectSearchBar() {
  const dispatch = useDispatch();
  const { term, catLabel } = useAppSelector(state => state.soundEffect);
  const mobile = useAppSelector(state => (state.navigation as { size: number }).size < SIZE_LG);
  const [input, setInput] = useState<string>(term);

  // The term can be set from outside the bar (arriving from the global search with a term in the
  // navigation query, or a reset): keep the input in sync with it.
  useEffect(() => setInput(term), [term]);

  const onSubmit = () => dispatch(setSoundEffectTerm(input.trim()));

  const onClear = () => {
    setInput('');
    dispatch(setSoundEffectTerm(''));
  };

  return (
    <div className="searchbar">
      <SearchInput
        value={input}
        onChange={event => setInput(event.target.value)}
        onClear={onClear}
        onSubmit={onSubmit}
        placeholder={I18n.t('soundEffects.search.placeholder')}
      >
        {catLabel ? (
          <div className="search-items">
            <div className="search-items--item">
              {catLabel}
              <button
                className="search-items--remove"
                type="button"
                onClick={() => dispatch(clearSoundEffectCategory())}
                aria-label={I18n.t('soundEffects.filters.clear')}
              >
                <i className="icon icon--clear" aria-hidden="true" />
              </button>
            </div>
          </div>
        ) : null}
      </SearchInput>
      {/* On mobile the category column has no room to stay open: same modal-opening button as music. */}
      {mobile ? (
        <button
          type="button"
          className="searchbar__button filter-button"
          onClick={() => dispatch(openFilterModal())}
        >
          <i className="icon icon--filter" aria-hidden="true" />
          <span className="sr-only">{I18n.t('search.filters.label')}</span>
        </button>
      ) : null}
    </div>
  );
}

